/**
 * Where a phone hangs, and what happened to it when it dropped (owner, 2026-10-10: 28 phones on USB hubs,
 * and finding the one that fell off meant going through all of them).
 *
 * The machine reports the USB port path ("1-4.2.3"), kept after the drop; whether the phone is still on
 * its cable; and how its bounded reconnect is going (only while on the cable, three tries at most).
 */

import { BatteryCharging, BatteryLow, BatteryMedium } from 'lucide-react'

import type { Device, UsbDrop } from '../../api/types'
import { useT } from '../../i18n'

const WEEK_MS = 7 * 24 * 3600 * 1000
/** at or under this, a phone that vanished most likely ran flat */
const FLAT_PCT = 10
const LOW_PCT = 20

function hhmm(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''
}

/**
 * Which hub and which place on it. The farm's hubs are 10-port boxes made of three 4-port chips in a chain,
 * the chain always on the chip's port 4 (seen on tiktok-uploader-01, 2026-10-10): "1-3.4.4.2" is the hub on
 * the computer's port 3, third chip, its port 2 → place 8. A path that does not follow that shape gets no
 * place, only the raw path.
 */
export function usbPlace(path?: string | null): { hub: string; place: number | null } | null {
  if (!path) return null
  const chain = path.split('-', 2)[1]
  if (!chain) return null
  const [root = '', ...rest] = chain.split('.')
  if (rest.length === 0) return { hub: root, place: null }       // a phone straight in the computer
  const last = Number(rest[rest.length - 1])
  const through = rest.slice(0, -1)
  if (!through.every((x) => x === '4') || !(last >= 1 && last <= 4)) return { hub: root, place: null }
  return { hub: root, place: through.length * 3 + last }
}

/** "hub 3 · place 8" for any phone, the raw path on hover. */
export function UsbSeat({ device: d }: { device: Device }) {
  const t = useT().machines.usb
  const where = usbPlace(d.usb_path)
  if (!d.usb_path || !where) return null
  const [bus = '', chain = ''] = d.usb_path.split('-', 2)
  return (
    <span className="usb-seat" title={`${t.port(d.usb_path)} · ${t.route(bus, chain.split('.'))}`}>
      {where.place != null ? t.seat(where.hub, where.place) : t.hubOnly(where.hub)}
    </span>
  )
}

/** "USB 1-4.2.3" with the route spelled out on hover. */
export function UsbPort({ path }: { path?: string | null }) {
  const t = useT().machines.usb
  if (!path) return null
  const [bus = '', chain = ''] = path.split('-', 2)
  return <span className="mono" title={t.portTitle(t.route(bus, chain.split('.')))}>{t.port(path)}</span>
}

/** What is wrong with an offline phone, in one line: where, since when, and what the machine is doing. */
export function UsbState({ device: d }: { device: Device }) {
  const t = useT().machines.usb
  if (d.status !== 'offline') return null
  const r = d.recovery
  // the last reading before it went dark, if it was nearly empty: it ran flat, a person presses its button
  const flat = d.usb_present !== true && d.battery != null && d.battery <= FLAT_PCT && !d.charging
  const state = flat ? t.ranFlat(d.battery!, hhmm(d.battery_at))
    : d.usb_present === false ? t.gone
    : r?.state === 'trying' ? t.trying(r.attempts ?? 1, r.max_attempts ?? 3)
    : r?.state === 'gave_up' ? t.gaveUp(r.max_attempts ?? 3)
    : r?.state === 'waiting' ? t.waiting
    : d.usb_present && d.adb_state && d.adb_state !== 'absent' ? t.stateHint(d.adb_state)
    : null
  if (!d.usb_path && !state && !d.offline_since) return null
  return (
    <div className="tiny warn-text usb-state">
      {usbPlace(d.usb_path)?.place != null ? <UsbSeat device={d} /> : <UsbPort path={d.usb_path} />}
      {d.offline_since && <span> · {t.since(hhmm(d.offline_since))}</span>}
      {state && <span> · {state}</span>}
    </div>
  )
}

export function recentDrops(d: Device, now = Date.now()): UsbDrop[] {
  return (d.drops ?? []).filter((x) => now - new Date(x.at).getTime() < WEEK_MS)
}

/** "3× in 7 days", with each drop on hover — a phone or a port that keeps dropping stands out. */
export function DropCount({ device }: { device: Device }) {
  const t = useT().machines.usb
  const drops = recentDrops(device)
  if (drops.length === 0) return null
  const how = (x: UsbDrop) => !x.back_at ? t.stillOut
    : x.fixed_by === 'hub' ? t.backByHub
    : x.unplugged ? t.backUnplugged
    : t.backSelf
  const lines = drops.slice().reverse().map((x) => t.dropLine(hhmm(x.at), how(x)) + (x.usb_path ? ` (${x.usb_path})` : ''))
  return <span className="tiny dim" title={lines.join('\n')}>↯ {t.drops(drops.length)}</span>
}

/** The battery of an online phone: level, charging, and a warning when it sits on a cable that does not keep
 *  up — that is the phone that will be found switched off (owner, 2026-10-10). */
export function BatteryChip({ device: d }: { device: Device }) {
  const t = useT().machines.usb
  if (d.status === 'offline' || d.battery == null) return null
  // on a cable and still *discharging*: the port does not keep up. Not "not_charging" — that is a Samsung
  // holding at 85% with battery protection on, which is fine.
  const weak = d.powered === true && d.battery_status === 'discharging'
  const low = d.battery <= LOW_PCT && !d.charging
  const Icon = d.charging ? BatteryCharging : low ? BatteryLow : BatteryMedium
  return (
    <span className={`battery-chip tiny${low || weak ? ' warn-text' : ' dim'}`}
          title={[d.charging ? t.charging : null, weak ? t.weakCharge : null, d.temperature != null ? `${Math.round(d.temperature)}°` : null]
            .filter(Boolean).join(' · ') || undefined}>
      <Icon className="ico" size={13} aria-hidden /> {t.level(d.battery)}
      {weak && <span> ⚠ {t.weakShort}</span>}
    </span>
  )
}
